import { useState } from "react";
import { OverlayTrigger, Popover } from "@rio-cloud/rio-uikit";
import CancelDispatchDialog from "./dialogs/cancelDispatchDialog";
import {
  Occurrence,
  OccurrenceDispatch,
} from "../../../../../../models/occurrence";
import { useDispatch } from "../../../../../../hooks/useDispatch";
import { makeOccurrenceDispatchAvailable } from "../../../../../../api/occurrence";
import ErrorDialog from "../../../../../dialogs/ErrorDialog";
import RefusalReasonDialog from "./dialogs/refusalReasonDialog";
import CreateTripDialog from "./dialogs/createTripDialog";
import useMediaQuery from "@mui/material/useMediaQuery";

interface DispatchCardProps {
  status?: string;
  occurrence: Occurrence | null;
  occurrenceDispatch: OccurrenceDispatch;
  cancelDispatch?: (
    occurrence: Occurrence,
    dispatch: OccurrenceDispatch,
    reason: string,
    description: string
  ) => Promise<{
    wasCanceled: boolean;
    error?: string;
    dispatch?: OccurrenceDispatch;
  }>;
  persona?: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
}

const DispatchCard = (props: DispatchCardProps) => {
  const {
    optionsOccurrenceType,
    optionsPayer,
    dealership,
    statusColor,
    newStatus,
    setNewStatus,
    showCancelDialog,
    setShowCancelDialog,
    route,
    showCancelReasonDialog,
    setShowCancelReasonDialog,
    setNewRefusal,
    newRefusal,
    drivers,
    newDriverName,
    setNewDriverName,
  } = useDispatch(props);

  const [errorDialog, setErrorDialog] = useState<string | null>(null);

  const [showCreateTripDialog, setShowCreateTripDialog] =
    useState<boolean>(false);
  const isResponsiveView = useMediaQuery("(max-width: 1024px)");

  return (
    <>
      <div className="p-6 mt-[10px] border rounded dark:bg-gray-800" style={{ borderColor: "#31b5c0" }}>
        <div className="flex flex-col gap-4 pb-[5px] w-full">
          <div
            className={`flex ${
              isResponsiveView 
                ? "flex-wrap gap-4"
                : "justify-between items-center"
            } gap-[25px] ${
              newStatus === "Disponível" ||
              newStatus === "Indisponível para atendimento"
                ? "w-[100%]"
                : "w-[98%]"
            }`}
          >
            {" "}
            <div
              className={`flex ${
                isResponsiveView 
                  ? "flex-col gap-4"
                  : "flex-row justify-start gap-6"
              }`}
            >
              <div className="flex gap-6 items-center">
                <div className="flex items-center justify-center p-2 border rounded w-[40px] h-[40px]">
                  <img
                    src="/assets/images/clipboard.svg"
                    alt=""
                    style={{ width: 18, height: 18 }}
                  />
                </div>
                <div
                  className={`flex flex-col ${
                    isResponsiveView  ? "w-full" : "w-[200px]"
                  }`}
                >
                  <span className="text-color-dark font-semibold text-size-14">
                    {optionsOccurrenceType.find(
                      (option) =>
                        option.id === props.occurrenceDispatch.occurrenceType
                    )?.label ?? "Socorro mecânico"}
                  </span>
                  <span className="text-color-dark text-size-14">
                    {(
                      props.occurrenceDispatch.createdAt ?? new Date()
                    ).toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </div>

              <div
                className={`flex flex-col ${
                  isResponsiveView  ? "w-full" : "w-[250px]"
                }`}
              >
                <span className="text-color-dark font-semibold text-size-14">
                  Protocolo de acionamento
                </span>
                <span className="text-color-dark text-size-14">-</span>
              </div>

              {/* Fonte pagadora */}
              {props.persona !== "MANAGER" && (
                <div
                  className={`flex flex-col ${
                    isResponsiveView  ? "w-full" : ""
                  }`}
                >
                  <span className="text-color-dark font-semibold text-size-14">
                    Fonte pagadora
                  </span>
                  <span className="text-color-dark text-size-14">
                    {optionsPayer.find(
                      (option) => option.id === props.occurrenceDispatch.payer
                    )?.label ?? ""}
                  </span>
                </div>
              )}
            </div>

            <div
              className={`flex gap-6 ${
                isResponsiveView  ? "flex-col mt-4 w-full" : "ml-auto"
              }`}
            >
              <div className="flex items-center justify-center px-[10px] gap-2 h-[48px] bg-[#F9FDFD] border-2 border-[#CEF0F3] rounded">
                <span className="rioglyph rioglyph-workshop bg-[#FFFFFF] p-2 border rounded"></span>
                <span className="font-semibold ml-2">{dealership?.name}</span>
              </div>
              <div className="flex items-center justify-center px-[10px] gap-2 h-[48px] bg-[#F9FDFD] border-2 border-[#CEF0F3] rounded">
                <span className="rioglyph rioglyph-pin bg-[#FFFFFF] p-2 border rounded"></span>
                <span className="font-semibold ml-2">
                  {[dealership?.city, dealership?.state]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end">
            {newStatus === "Aguardando concessionária" &&
              !["MANAGER"].includes(props.persona ?? "") && (
                <>
                  <div
                    className={
                      isResponsiveView 
                        ? "flex flex-col gap-2 mb-4"
                        : "hidden"
                    }
                  >
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={async () => {
                        if (props.occurrence && props.occurrenceDispatch.id) {
                          const updated = await makeOccurrenceDispatchAvailable(
                            props.occurrence.id,
                            props.occurrenceDispatch.id
                          );
                          if (updated) setNewStatus("Disponível");
                        } else {
                          setErrorDialog(
                            "Ocorreu um erro ao confirmar acionamento"
                          );
                        }
                      }}
                    >
                      Confirmar acionamento
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowCancelDialog(true)}
                    >
                      Cancelar acionamento
                    </button>

                    <div className="flex mt-2 gap-2">
                      <div className="flex items-center justify-center px-[10px] gap-2 h-[48px] bg-[#F9FDFD] border-2 border-[#CEF0F3] rounded">
                        <span className="rioglyph rioglyph-workshop bg-[#FFFFFF] p-2 border rounded"></span>
                        <span className="font-semibold ml-2">
                          {dealership?.name}
                        </span>
                      </div>
                      <div className="flex items-center justify-center px-[10px] gap-2 h-[48px] bg-[#F9FDFD] border-2 border-[#CEF0F3] rounded">
                        <span className="rioglyph rioglyph-pin bg-[#FFFFFF] p-2 border rounded"></span>
                        <span className="font-semibold ml-2">
                          {[dealership?.city, dealership?.state]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: isResponsiveView ? "none" : "block" }}>
                    <OverlayTrigger
                      trigger="click"
                      key="left"
                      placement="left"
                      overlay={
                        <Popover
                          id="popover-positioned-left"
                          className="w-[250px]"
                        >
                          <div
                            className="popover-item flex items-center cursor-pointer w-[250px]"
                            onClick={async () => {
                              if (props.occurrence && props.occurrenceDispatch.id) {
                                const updated =
                                  await makeOccurrenceDispatchAvailable(
                                    props.occurrence.id,
                                    props.occurrenceDispatch.id
                                  );
                                if (updated) setNewStatus("Disponível");
                              } else {
                                setErrorDialog(
                                  "Ocorreu um erro ao confirmar acionamento"
                                );
                              }
                            }}
                          >
                            <span className="flex items-center justify-center text-color-darkest text-size-h4 rioglyph rioglyph-ok-sign"></span>
                            <span className="text-color-darkest text-size-14 font-semibold">
                              Confirmar acionamento
                            </span>
                          </div>
                          <div
                            className="popover-item flex items-center cursor-pointer w-[250px]"
                            onClick={() => setShowCancelDialog(true)}
                          >
                            <span className="flex items-center justify-center text-color-darkest text-size-h4 rioglyph rioglyph-eye-option"></span>
                            <span className="text-color-darkest text-size-14 font-semibold">
                              Cancelar acionamento
                            </span>
                          </div>
                        </Popover>
                      }
                    >
                      <span className="rioglyph rioglyph-option-vertical cursor-pointer"></span>
                    </OverlayTrigger>
                  </div>
                </>
              )}
          </div>

          <hr className="border-gray-200 my-2" />

          <div className="flex flex-col gap-4 mt-5 w-full">
            <div className={`flex ${isResponsiveView  ? "flex-col gap-4" : "flex-row items-center justify-between"} w-full`}>
              <div className={`flex ${isResponsiveView  ? "flex-col gap-4" : "flex-row items-center gap-6"}`}>
                <span className="flex items-center justify-center border rounded-full bg-[#F3F4F7] w-[40px] h-[40px]">
                  <span className="rioglyph rioglyph-truck text-[#667085] text-size-14"></span>
                </span>

                <div className={`flex flex-col ${isResponsiveView  ? "w-full" : "w-[200px]"}`}>
                  <div className={`flex ${isResponsiveView  ? "flex-col" : "gap-[20px]"} mb-1`}>
                    <span className="text-size-14 text-color-dark dark:text-white">
                      <span className="font-semibold">Modelo:</span>{" "}
                      {props.occurrence?.vehicleModel || "-"}
                    </span>
                    <span className="text-size-14 text-color-dark dark:text-white">
                      <span className="font-semibold">Placa:</span>{" "}
                      {props.occurrence?.vehicleLicensePlate || "-"}
                    </span>
                  </div>
                  <span className="text-size-14 text-color-dark dark:text-white">
                    <span className="font-semibold">Chassi:</span>{" "}
                    {props.occurrence?.vehicleChassis || "-"}
                  </span>
                </div>

                <div className={`flex flex-col ${isResponsiveView  ? "w-full" : "w-[250px]"}`}>
                  <span className="text-size-14 font-semibold text-color-dark dark:text-white">
                    Localização do veículo
                  </span>
                  <span className="text-size-14 text-color-dark dark:text-gray-300 whitespace-nowrap overflow-hidden text-ellipsis route-destination">
                    {route?.destination || "-"}
                  </span>
                </div>

                <div className={`flex flex-col ${isResponsiveView  ? "w-full" : ""}`}>
                  <span className="text-size-14 font-semibold text-color-dark dark:text-white">
                    Mecânico
                  </span>
                  <span className="text-size-14 dark:text-gray-300">{newDriverName ?? "-"}</span>
                </div>
              </div>

              <div className={`flex ${isResponsiveView  ? "flex-col gap-4 w-full" : "flex-col items-end gap-2"}`}>
                {newStatus === "Indisponível para atendimento" && (
                  <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => setShowCancelReasonDialog(true)}
                  >
                    <span className="rioglyph rioglyph-clipboard text-[18px] text-[#A7AFBB]"></span>
                    <span className="text-[#2A3740] dark:text-white underline text-size-14">
                      Visualizar parecer
                    </span>
                  </div>
                )}

                <span
                  className={`px-[10px] py-[4px] bg-[#F5F8FB] dark:bg-gray-700 border rounded-full text-size-14 ${isResponsiveView  ? "text-center": ""}`}
                  style={{
                    borderColor: statusColor(newStatus),
                    color: statusColor(newStatus),
                  }}
                >
                  {newStatus}
                </span>

                {newStatus === "Disponível" &&
                  !["MANAGER"].includes(props.persona ?? "") && (
                    <button
                      className={`btn btn-default btn-sm ${isResponsiveView  ? "w-full" : "w-auto"}`}
                      onClick={() =>
                        window.open(
                          `${import.meta.env.VITE_ROUTING_URL}/#/routing/trips/schedule?id=${props.occurrenceDispatch.route}&trip=true&clean=true`,
                          "_blank"
                        )
                      }
                    >
                      Atribuir{" "}
                      {props.occurrenceDispatch.occurrenceType?.includes("Socorro") ||
                      props.occurrenceDispatch.occurrenceType?.includes("ASSISTANCE")
                        ? "mecânico"
                        : "guincho"}
                    </button>
                  )}
              </div>
            </div>
          </div>
        </div>  
        </div>
      <CancelDispatchDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        occurrence={props.occurrence}
        occurrenceDispatch={props.occurrenceDispatch}
        cancelDispatch={props.cancelDispatch}
        updateStatus={(status, reason, description) => {
          setNewStatus(status);
          setNewRefusal({ reason, description });
        }}
      />

      <RefusalReasonDialog
        show={showCancelReasonDialog}
        onClose={() => setShowCancelReasonDialog(false)}
        newRefusal={newRefusal ?? { reason: "", description: "" }}
      />

      <ErrorDialog
        show={!!errorDialog}
        onClose={() => setErrorDialog(null)}
        title="Ops! Ocorreu um erro"
        body={errorDialog}
      />

      <CreateTripDialog
        show={showCreateTripDialog}
        onClose={() => setShowCreateTripDialog(false)}
        occurrence={props.occurrence}
        occurrenceDispatch={props.occurrenceDispatch}
        routeId={props.occurrenceDispatch.route ?? ""}
        onTripCreated={(driverName: string) => {
          setNewStatus("Disponível");
          setNewDriverName(driverName);
        }}
        drivers={drivers}
      />
    </>
  );
};

export default DispatchCard;
